import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/layout/AppShell';
import LoginPage from '@/pages/LoginPage';
import OnboardingPage from '@/pages/OnboardingPage';
import DashboardPage from '@/pages/DashboardPage';
import PipelinePage from '@/pages/PipelinePage';
import ContactDetailPage from '@/pages/ContactDetailPage';
import SettingsPage from '@/pages/SettingsPage';
import NotFoundPage from '@/pages/NotFoundPage';
import DevelopersPage from '@/pages/DevelopersPage';
import DeveloperDetailPage from '@/pages/DeveloperDetailPage';
import PropertyManagerDetailPage from '@/pages/PropertyManagerDetailPage';
import ContractsPage from '@/pages/ContractsPage';
import MarketingPage from '@/pages/MarketingPage';

function Protected({ children }) {
    const { session, loading, profile, profileLoading } = useAuth();
    if (loading || profileLoading) return (
        <div className="flex h-screen items-center justify-center bg-[#f7f8fa]" data-testid="app-loading">
            <div className="flex items-center gap-3"><div className="h-3 w-3 rounded-full bg-aks-gold animate-pulse" /><span className="text-sm font-medium tracking-wide text-aks-navy">Loading AKSAAF CRM…</span></div>
        </div>
    );
    if (!session) return <Navigate to="/login" replace />;
    if (!profile?.organization_id) return <Navigate to="/onboarding" replace />;
    return children;
}
function Public({ children }) {
    const { session, loading, profile } = useAuth();
    if (loading) return null;
    if (session && profile?.organization_id) return <Navigate to="/dashboard" replace />;
    if (session && !profile?.organization_id) return <Navigate to="/onboarding" replace />;
    return children;
}
function OnboardingGate({ children }) {
    const { session, loading, profile } = useAuth();
    if (loading) return null;
    if (!session) return <Navigate to="/login" replace />;
    if (profile?.organization_id) return <Navigate to="/dashboard" replace />;
    return children;
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster position="bottom-right" richColors closeButton theme="light" toastOptions={{ style: { fontFamily: 'Inter', borderRadius: 12 } }} />
                <Routes>
                    <Route path="/login" element={<Public><LoginPage /></Public>} />
                    <Route path="/onboarding" element={<OnboardingGate><OnboardingPage /></OnboardingGate>} />
                    <Route element={<Protected><AppShell /></Protected>}>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/pipeline" element={<PipelinePage />} />
                        <Route path="/pipeline/:contactId" element={<ContactDetailPage />} />
                        <Route path="/developers" element={<DevelopersPage />} />
                        <Route path="/developers/pms" element={<DevelopersPage />} />
                        <Route path="/developers/:developerId" element={<DeveloperDetailPage />} />
                        <Route path="/property-managers/:pmId" element={<PropertyManagerDetailPage />} />
                        <Route path="/contracts" element={<ContractsPage />} />
                        <Route path="/contracts/:tab" element={<ContractsPage />} />
                        <Route path="/marketing" element={<MarketingPage />} />
                        <Route path="/marketing/:tab" element={<MarketingPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/settings/:tab" element={<SettingsPage />} />
                    </Route>
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}
export default App;
